import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { OidcCallbackHandler, OidcLoginRedirect } from '@nohara/react-oidc';

import { default as AdminRoot, ErrorBoundary as AdminErrorBoundary } from './routes/admin/root';
import { default as OrgRoot, ErrorBoundary as OrgErrorBoundary } from './routes/org/root';
import AppRoot, { ErrorBoundary as AppErrorBoundary } from './routes/root';

import { LazyLayout, LoadingScreen } from '@/components/ui';
import { paths } from '@/config/paths';

const convert = (queryClient: QueryClient) => (m: any) => {
  const { clientLoader, clientAction, default: Component, ...rest } = m;
  return {
    ...rest,
    loader: clientLoader?.(queryClient),
    action: clientAction?.(queryClient),
    Component,
  };
};

export const createAppRouter = (queryClient: QueryClient) =>
  createBrowserRouter([
    {
      path: paths.auth.callback.path,
      element: (
        <OidcCallbackHandler
          successPath={paths.home.path}
          errorPath={paths.error.path}
          element={<LoadingScreen />}
        />
      ),
    },
    {
      path: paths.auth.login.path,
      element: <OidcLoginRedirect element={<LoadingScreen />} />,
    },
    {
      path: paths.auth.logout.path,
      element: <OidcLoginRedirect element={<LoadingScreen />} />,
    },
    {
      path: paths.home.path,
      element: <AppRoot />,
      ErrorBoundary: AppErrorBoundary,
      children: [
        {
          path: '',
          element: <Navigate to={paths.org.root.path} />,
        },
        {
          path: paths.admin.root.path,
          element: <AdminRoot />,
          ErrorBoundary: AdminErrorBoundary,
          HydrateFallback: LoadingScreen,
          children: [
            {
              path: paths.admin.dashboard.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/dashboard').then(convert(queryClient)),
            },
            {
              path: paths.admin.users.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/users').then(convert(queryClient)),
            },
            {
              path: paths.admin.orgs.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/orgs').then(convert(queryClient)),
            },
            {
              path: paths.admin.roles.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/roles').then(convert(queryClient)),
            },
            {
              path: paths.admin.apps.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/apps').then(convert(queryClient)),
            },
            {
              path: paths.admin.app.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/app').then(convert(queryClient)),
            },
            {
              path: paths.admin.profile.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/profile').then(convert(queryClient)),
            },
            {
              path: paths.admin.user.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/user').then(convert(queryClient)),
            },
          ],
        },
        {
          path: paths.org.root.path,
          element: <OrgRoot />,
          ErrorBoundary: OrgErrorBoundary,
          children: [
            {
              path: '',
              element: <Navigate to={paths.org.select.path} />,
            },
            {
              path: paths.org.select.path,
              HydrateFallback: LoadingScreen,
              lazy: () => import('./routes/org/select').then(convert(queryClient)),
            },
            {
              path: paths.onboarding.path,
              HydrateFallback: LoadingScreen,
              lazy: () => import('./routes/org/onboarding').then(convert(queryClient)),
            },
            {
              path: paths.org.userProfile.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/org/user').then(convert(queryClient)),
            },
            {
              path: paths.org.selected.path,
              children: [
                {
                  path: paths.org.dashboard.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/dashboard').then(convert(queryClient)),
                },
                {
                  path: paths.org.userProfile.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/user').then(convert(queryClient)),
                },
                {
                  path: paths.org.users.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/users').then(convert(queryClient)),
                },
                {
                  path: paths.org.settings.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/settings').then(convert(queryClient)),
                },
                {
                  path: paths.org.roles.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/roles').then(convert(queryClient)),
                },
                {
                  path: paths.org.apps.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/apps').then(convert(queryClient)),
                },
                {
                  path: paths.org.app.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/app').then(convert(queryClient)),
                },
                {
                  path: paths.org.user.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/user').then(convert(queryClient)),
                },
                {
                  path: paths.org.orgProfile.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/org').then(convert(queryClient)),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: '*',
      HydrateFallback: LoadingScreen,
      lazy: () => import('./routes/not-found').then(convert(queryClient)),
    },
  ]);

export const AppRouter = () => {
  const queryClient = useQueryClient();

  const router = useMemo(() => createAppRouter(queryClient), [queryClient]);

  return <RouterProvider router={router} />;
};
